import React, { useMemo, useState } from 'react';
import { UgcAdProject, UgcAdScene } from '../../services/api/ugcAdService';
import { UgcAdSceneDraft } from './types';
import { TaskyButton, TaskyTextarea, TaskyBadge, TaskyCard, TaskyAlert, TaskyInput, TaskyConfirmDialog, useConfirm } from '../tasky';
import { Sparkles, Save, CheckCircle2, Wand2, SlidersHorizontal, Plus, Trash2, Clock } from 'lucide-react';
import { CAST_AGE_OPTIONS, CAST_GENDER_OPTIONS, FORMAT_TYPES } from './UgcAdSettingsForm';
import GenerationProgress from './GenerationProgress';

/**
 * ステップ②: 構成案（字コンテ＋セリフ）。
 *
 * 高価な画像・動画生成に入る前に、テキストだけで構成を確定させるステップ
 * （TopViewのストーリーボード承認ゲートと同じ「安い決定を先に」の設計。
 * 歌広告の②歌詞に相当するが、UGCではフィックスした時点でシーンがそのまま確定する）。
 *
 * - シーンカード = 役割（フック等）+ セリフ/ナレーション + 映像イメージ（字コンテ）+ 推定尺
 * - セリフ・字コンテはローカルドラフトで編集し、「保存して次へ」でまとめてPUT→フィックス
 * - シーンの追加/削除は構造変更なので即PUT（song-ad StoryboardStepと同じB4仕様）
 * - AI修正チップ＋自由指示 → generate-script(instruction) で全体を作り直す。
 *   冒頭3秒（フック）は勝敗を分けるため、専用チップを先頭に置く（他社比較の結論）
 */

interface ScriptStepProps {
  project: UgcAdProject;
  scenes: UgcAdScene[];
  sceneDrafts: UgcAdSceneDraft[];
  isGenerating: boolean;
  isSaving: boolean;
  isReordering?: boolean;
  onGenerate: (instruction?: string) => Promise<void>;
  onSceneDialogueChange: (sceneId: string, text: string) => void;
  onSceneDirectionChange: (sceneId: string, text: string) => void;
  onSaveAndFix: () => Promise<void>;
  onAddScene: () => void;
  onDeleteScene: (sceneId: string) => void;
  /** 「設定を変更」導線。①情報登録タブへ */
  onEditSettings?: () => void;
}

const AI_CHIPS = ['冒頭のつかみ（フック）を強く', 'もっと自然な話し言葉に', '商品名を増やす'];

/** 構成案生成の処理段階（クライアント側の演出。backendは細かい進捗を返さない） */
const SCRIPT_STAGES = [
  '商品の訴求軸を整理しています',
  'ターゲットに刺さる切り口を選んでいます',
  'シーンを構成しています',
  'セリフを書いています',
];

const ScriptStep: React.FC<ScriptStepProps> = ({
  project,
  scenes,
  sceneDrafts,
  isGenerating,
  isSaving,
  isReordering = false,
  onGenerate,
  onSceneDialogueChange,
  onSceneDirectionChange,
  onSaveAndFix,
  onAddScene,
  onDeleteScene,
  onEditSettings,
}) => {
  const confirm = useConfirm();
  const [aiInstruction, setAiInstruction] = useState('');
  const [pendingInstruction, setPendingInstruction] = useState<null | { instruction?: string }>(null);
  const [showFixConfirm, setShowFixConfirm] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);

  const hasScenes = scenes.length > 0;
  /** 絵コンテ/クリップが既にあるか。構成案の作り直しはそれらを失効させるため確認を挟む */
  const hasDownstream = scenes.some((s) => !!s.storyboard.image || !!s.clip.video);

  const formatLabel = FORMAT_TYPES.find((f) => f.key === project.format_type)?.label ?? project.format_type;
  const genderLabel = CAST_GENDER_OPTIONS.find((g) => g.key === project.cast_gender)?.label ?? project.cast_gender;
  const ageLabel = CAST_AGE_OPTIONS.find((a) => a.key === project.cast_age)?.label ?? project.cast_age;
  const summaryChips = [
    formatLabel,
    `出演者: ${genderLabel}${project.cast_age !== 'auto' ? `・${ageLabel}` : ''}`,
    `${project.target_seconds}秒`,
    '9:16',
  ];

  const totalEstimated = useMemo(
    () => Math.round(scenes.reduce((sum, s) => sum + (s.duration_sec || 0), 0)),
    [scenes]
  );

  const runGuarded = (instruction?: string) => {
    if (hasDownstream) {
      setPendingInstruction({ instruction });
      return;
    }
    onGenerate(instruction);
  };

  const handleAiFreeSubmit = () => {
    const instruction = aiInstruction.trim();
    if (!instruction) return;
    runGuarded(instruction);
    setAiInstruction('');
  };

  const handleConfirmedRegenerate = () => {
    if (!pendingInstruction) return;
    onGenerate(pendingInstruction.instruction);
    setPendingInstruction(null);
  };

  /**
   * 「構成を確定して次へ」。レビューP0/P1対応:
   * - セリフ・映像イメージの両方が空のシーンはストーリーボードを生成できず⑥で詰むため、
   *   確定前に検出してブロックする（入力するか削除してもらう）
   * - 確定は後続のストーリーボード自動生成（クレジット消費）の引き金なので、
   *   確認ダイアログで消費とテロップ作り直しを事前に予告する（従来は事後トーストのみだった）
   */
  const pendingStoryboardCount = scenes.filter((s) => s.storyboard.status === 'none').length;

  const handleFixClick = () => {
    const emptyIndexes = sceneDrafts
      .map((d, i) => (!d.dialogue.trim() && !d.direction.trim() ? i + 1 : null))
      .filter((n): n is number => n !== null);
    if (emptyIndexes.length > 0) {
      setFixError(
        `シーン${emptyIndexes.join('・')}のセリフ・映像イメージが空です。内容を入力するか、シーンを削除してから確定してください。`
      );
      return;
    }
    setFixError(null);
    setShowFixConfirm(true);
  };

  const handleConfirmedFix = () => {
    setShowFixConfirm(false);
    onSaveAndFix();
  };

  return (
    <div className="space-y-5">
      {/* 設定サマリー */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
        {summaryChips.map((t, i) => (
          <span key={`${i}-${t}`} className="bg-warm-surface rounded-full px-2.5 py-1">
            {t}
          </span>
        ))}
        {onEditSettings && (
          <button
            type="button"
            onClick={onEditSettings}
            className="flex items-center gap-1 text-primary hover:underline ml-1"
          >
            <SlidersHorizontal className="w-3 h-3" />
            設定を変更
          </button>
        )}
      </div>

      <TaskyCard variant="warm" padding="md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-dark">構成案</h3>
          {project.script_fixed_at && (
            <TaskyBadge variant="success">
              <CheckCircle2 className="w-3 h-3" />
              確定済み
            </TaskyBadge>
          )}
        </div>

        {/* 何を処理しているか・どれくらい待つかを出す（UGC-02） */}
        <GenerationProgress
          active={isGenerating}
          stages={SCRIPT_STAGES}
          estimate="通常30秒ほどかかります"
          className="mb-3"
        />

        {!hasScenes ? (
          <div className="text-center py-10 text-gray-400 text-sm border border-dashed border-warm-border rounded-tasky-md">
            <TaskyButton onClick={() => onGenerate()} isLoading={isGenerating}>
              <Sparkles className="w-4 h-4" />
              構成案を生成
            </TaskyButton>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-3">
              セリフはそのままテロップとナレーションになります。
            </p>

            <div className="space-y-3">
              {scenes.map((scene, index) => {
                const draft = sceneDrafts.find((d) => d.id === scene.id);
                return (
                  <div key={scene.id} className="bg-white border border-warm-border rounded-xl p-3.5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-gray-700">シーン {index + 1}</span>
                      {scene.beat && (
                        <span className="text-[10px] font-semibold tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">
                          {scene.beat}
                        </span>
                      )}
                      <span className="flex items-center gap-0.5 text-[11px] text-gray-400">
                        <Clock className="w-3 h-3" />
                        約{Math.round(scene.duration_sec || 0)}秒
                      </span>
                      <div className="ml-auto">
                        <button
                          type="button"
                          onClick={async () => {
                            if (scenes.length <= 2) return;
                            if (await confirm({ title: `シーン${index + 1}を削除しますか？残りのシーンで尺を自動再配分します。`, tone: 'destructive', confirmText: '削除する' })) {
                              onDeleteScene(scene.id);
                            }
                          }}
                          disabled={scenes.length <= 2 || isReordering}
                          title={scenes.length <= 2 ? 'シーンは2つ以上必要です' : '削除'}
                          className="p-1.5 text-gray-400 hover:text-destructive transition-colors disabled:opacity-40"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-0.5">セリフ・ナレーション</label>
                        <TaskyTextarea
                          value={draft?.dialogue ?? ''}
                          onChange={(e) => onSceneDialogueChange(scene.id, e.target.value)}
                          rows={3}
                          maxLength={500}
                          disabled={isGenerating}
                          placeholder="例: 正直、最初は半信半疑でした。"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-0.5">映像イメージ（字コンテ）</label>
                        <TaskyTextarea
                          value={draft?.direction ?? ''}
                          onChange={(e) => onSceneDirectionChange(scene.id, e.target.value)}
                          rows={3}
                          maxLength={500}
                          disabled={isGenerating}
                          placeholder="例: 自宅のリビングでスマホを片手にカメラ目線で話す20代女性"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-3">
              <TaskyButton variant="outline" size="sm" onClick={onAddScene} disabled={isReordering || isGenerating}>
                <Plus className="w-3.5 h-3.5" />
                シーンを追加
              </TaskyButton>
              <span className="text-[11px] text-gray-400">
                推定合計 約{totalEstimated}秒 / 目標{project.target_seconds}秒
              </span>
            </div>

            {/* AI修正チップ */}
            <div className="mt-4 pt-4 border-t border-warm-border">
              <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                <Wand2 className="w-3.5 h-3.5 text-primary" />
                AIで構成案を作り直す
                <span className="font-normal text-gray-400">（全シーンを新しく書き直します。編集中の内容は保存してから反映されます）</span>
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {AI_CHIPS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    disabled={isGenerating}
                    onClick={() => runGuarded(label)}
                    className="flex items-center gap-1 rounded-full pl-2.5 pr-3.5 py-1.5 text-xs text-gray-600 border border-warm-border hover:border-primary transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <TaskyInput
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  placeholder="例: 冒頭を悩みの共感から始めて、最後は限定オファーで締める"
                  disabled={isGenerating}
                  className="flex-1"
                />
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={handleAiFreeSubmit}
                  disabled={!aiInstruction.trim() || isGenerating}
                  isLoading={isGenerating}
                >
                  適用
                </TaskyButton>
              </div>
            </div>

            {fixError && (
              <TaskyAlert tone="error" className="mt-4">
                {fixError}
              </TaskyAlert>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <TaskyButton
                onClick={handleFixClick}
                isLoading={isSaving}
                disabled={isGenerating || sceneDrafts.every((d) => !d.dialogue.trim() && !d.direction.trim())}
              >
                <Save className="w-4 h-4" />
                構成を確定して次へ
              </TaskyButton>
              <TaskyButton variant="ghost" onClick={() => runGuarded()} isLoading={isGenerating}>
                <Sparkles className="w-4 h-4" />
                作り直す
              </TaskyButton>
              {pendingStoryboardCount > 0 && (
                <span className="text-[11px] text-gray-400">
                  確定するとシーン{pendingStoryboardCount}件の画像生成が自動で始まります（クレジットを消費）
                </span>
              )}
            </div>
          </>
        )}
      </TaskyCard>

      <TaskyConfirmDialog
        open={!!pendingInstruction}
        onClose={() => setPendingInstruction(null)}
        onConfirm={handleConfirmedRegenerate}
        tone="warning"
        title="構成案を作り直しますか？"
        description="構成案を作り直すと、生成済みのストーリーボード・クリップは作り直しが必要になります。よろしいですか？"
        confirmText="作り直す"
      />

      <TaskyConfirmDialog
        open={showFixConfirm}
        onClose={() => setShowFixConfirm(false)}
        onConfirm={handleConfirmedFix}
        tone="warning"
        title="構成案を確定しますか？"
        description={
          pendingStoryboardCount > 0
            ? `確定すると、シーン${pendingStoryboardCount}件のストーリーボード画像の生成が自動で始まります（クレジットを消費します）。セリフはそのままテロップになり、変更した場合はテロップも作り直されます。`
            : 'セリフを変更した場合は、テロップも作り直されます。'
        }
        confirmText="確定して次へ"
      />
    </div>
  );
};

export default ScriptStep;
